/**
 * REFACTORED Feature Management Components
 * 
 * This file has been refactored to implement the new Global Feature Management System.
 * 
 * Key Changes:
 * - FeatureList: New unified component using /admin/features endpoint
 * - FeatureStats: Dashboard component displaying feature statistics
 * - Supports global feature toggles (enable for ALL users)
 * - Better status display (global, user-specific, disabled)
 * - Action buttons for toggle-global, manage-users, disable-all
 * - React Admin optimized with proper filtering and pagination
 * 
 * Components:
 * - FeatureStats: Dashboard statistics component
 * - FeatureList: Main features management interface
 * - FeatureTargetCreate: Legacy create form (backward compatibility)
 * - GlobalToggleButton: Toggle global status for features
 * - ManageUsersButton: Navigate to user management for non-global features
 * - DisableAllButton: Completely disable features
 * 
 * Related Files:
 * - FeatureUsers.js: Manages users for specific features when not global
 * - constants/Feature.js: Updated with descriptions and global-manageable features
 * 
 * API Endpoints Used:
 * - GET /admin/features - List all features with unified status
 * - GET /admin/features/stats - Get dashboard statistics
 * - PUT /admin/features/:id - Update feature status (toggle-global, disable-all)
 * - GET /admin/features/:feature/users - Manage users for non-global features
 */

import React, { useState, useEffect } from 'react';
import {
  List,
  Datagrid,
  Create,
  SimpleForm,
  TextField,
  ReferenceInput,
  AutocompleteInput,
  SelectInput,
  FileInput,
  FileField,
  FunctionField,
  useUpdate,
  useRefresh,
  useNotify,
  Filter,
  TextInput,
  BooleanInput,
  TopToolbar,
  CreateButton,
  ExportButton,
  DateField,
  required,
} from 'react-admin';
import { 
  Chip, 
  Box, 
  Typography, 
  IconButton, 
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Badge,
  Button,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Avatar
} from '@mui/material';
import {
  ToggleOn,
  ToggleOff,
  People,
  Public,
  Settings,
  Assessment,
  PersonAdd,
  Block,
  Refresh
} from '@mui/icons-material';
import httpClient from '../utils/httpClient';
import { FEATURES } from '../constants/Feature';

const apiUrl = process.env.REACT_APP_BACKEND_URL;

// Feature Stats Dashboard Component
const FeatureStats = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const refresh = useRefresh();

  const fetchStats = async () => {
    try {
      setLoading(true);
      const { json } = await httpClient(`${apiUrl}/features/stats`);
      setStats(json);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch feature stats:', err);
      setError('Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const handleRefresh = () => {
    fetchStats();
    refresh(); // Also refresh the main list
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" padding={2} marginBottom={2}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Card style={{ marginBottom: 16, backgroundColor: '#fff3cd', border: '1px solid #ffeaa7' }}>
        <CardContent>
          <Typography color="textSecondary">
            {error}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (!stats) {
    return null;
  }

  const statCards = [
    {
      title: 'Total Features',
      value: stats.totalFeatures,
      icon: <Assessment />,
      color: '#1976d2',
      description: 'Available features'
    },
    {
      title: 'Globally Enabled',
      value: stats.globallyEnabled,
      icon: <Public />,
      color: '#4caf50',
      description: 'Features enabled for all users'
    },
    {
      title: 'User-Specific',
      value: stats.userSpecific,
      icon: <People />,
      color: '#ff9800',
      description: 'Features with targeted users'
    },
    {
      title: 'Disabled',
      value: stats.disabled,
      icon: <Block />,
      color: '#f44336',
      description: 'Completely disabled features'
    },
    {
      title: 'Total User Targets',
      value: stats.totalUserTargets,
      icon: <PersonAdd />,
      color: '#9c27b0',
      description: 'Individual user assignments'
    }
  ];

  return (
    <Box marginBottom={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={2}>
        <Typography variant="h6">
          Feature Management Dashboard
        </Typography>
        <Tooltip title="Refresh Statistics">
          <IconButton onClick={handleRefresh} disabled={loading} size="small">
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>
      <Grid container spacing={2}>
        {statCards.map((card, index) => (
          <Grid item xs={6} sm={4} md={2.4} key={index}>
            <Card style={{ height: '100%', transition: 'transform 0.2s, box-shadow 0.2s' }}>
              <CardContent style={{ padding: '12px', '&:last-child': { paddingBottom: '12px' } }}>
                <Box display="flex" alignItems="center" marginBottom={0.5}>
                  <Avatar style={{ backgroundColor: card.color, marginRight: 8, width: 32, height: 32 }}>
                    {React.cloneElement(card.icon, { style: { fontSize: 18 } })}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" style={{ fontWeight: 'bold', color: card.color, margin: 0 }}>
                      {card.value}
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="body2" style={{ fontWeight: 'medium', marginBottom: 2 }}>
                  {card.title}
                </Typography>
                <Typography variant="caption" color="textSecondary" style={{ fontSize: '0.7rem' }}>
                  {card.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

// Status chip colors based on feature status
const getStatusColor = (status) => {
  switch (status) {
    case 'global': return 'success';
    case 'user-specific': return 'primary';
    case 'disabled': return 'default';
    default: return 'default';
  }
};

// Global Toggle Button Component
const GlobalToggleButton = ({ record, onStatsUpdate }) => {
  const [update] = useUpdate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [loading, setLoading] = useState(false);

  const handleToggleGlobal = async () => {
    setLoading(true);
    try {
      await update('features', record.id, { 
        action: 'toggle-global' 
      });
      notify(
        record.enabledGlobally 
          ? `${record.name} disabled globally` 
          : `${record.name} enabled globally`,
        { type: 'success' }
      );
      refresh();
      // Trigger stats refresh if callback provided
      if (onStatsUpdate) {
        setTimeout(onStatsUpdate, 500); // Small delay to ensure update is processed
      }
    } catch (error) {
      notify('Error toggling global status', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Tooltip title={record.enabledGlobally ? "Disable Global" : "Enable Global"}>
      <IconButton
        onClick={handleToggleGlobal}
        disabled={loading}
        color={record.enabledGlobally ? "success" : "default"}
        size="small"
      >
        {record.enabledGlobally ? <ToggleOn /> : <ToggleOff />}
      </IconButton>
    </Tooltip>
  );
};

// Manage Users Button Component
const ManageUsersButton = ({ record }) => {
  const handleManageUsers = () => {
    // Navigate to the custom feature users page using the API route pattern
    window.location.href = `#/admin/features/${record.id}/users`;
  };

  if (!record.canManageUsers) {
    return (
      <Tooltip title="Users cannot be managed for globally enabled features">
        <span>
          <IconButton disabled size="small">
            <People />
          </IconButton>
        </span>
      </Tooltip>
    );
  }

  return (
    <Tooltip title="Manage Users">
      <IconButton
        onClick={handleManageUsers}
        color="primary"
        size="small"
      >
        <Badge badgeContent={record.userCount} color="secondary">
          <People />
        </Badge>
      </IconButton>
    </Tooltip>
  );
};

// Disable All Button Component
const DisableAllButton = ({ record, onStatsUpdate }) => {
  const [update] = useUpdate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [open, setOpen] = useState(false);

  const handleDisableAll = async () => {
    try {
      await update('features', record.id, { 
        action: 'disable-all' 
      });
      notify(`${record.name} completely disabled`, { type: 'success' });
      refresh();
      setOpen(false);
      // Trigger stats refresh if callback provided
      if (onStatsUpdate) {
        setTimeout(onStatsUpdate, 500); // Small delay to ensure update is processed
      }
    } catch (error) {
      notify('Error disabling feature', { type: 'error' });
    }
  };

  if (record.status === 'disabled') {
    return null;
  }

  return (
    <>
      <Tooltip title="Disable Completely">
        <IconButton
          onClick={() => setOpen(true)}
          color="error"
          size="small"
        >
          <Settings />
        </IconButton>
      </Tooltip>
      
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>Disable Feature Completely</DialogTitle>
        <DialogContent>
          <Typography>
            This will disable "{record.name}" for ALL users (both global and specific users).
            This action cannot be undone easily.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleDisableAll} color="error">
            Disable All
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// Custom filters for the feature list
const FeatureFilters = (props) => (
  <Filter {...props}>
    <TextInput source="name" label="Feature Name" alwaysOn />
    <SelectInput 
      source="status" 
      label="Status" 
      choices={[
        { id: 'global', name: 'Global' },
        { id: 'user-specific', name: 'User-Specific' },
        { id: 'disabled', name: 'Disabled' }
      ]}
      emptyText="All"
    />
    <BooleanInput source="enabledGlobally" label="Globally Enabled" />
  </Filter>
);

// Custom Actions for the list
const FeatureListActions = ({ basePath, total, resource }) => (
  <TopToolbar>
    <CreateButton basePath={basePath} label="Add User to Feature" />
    <ExportButton total={total} resource={resource} />
  </TopToolbar>
);

// Enhanced Features List Component with integrated stats
const FeatureListWithStats = (props) => {
  const [statsKey, setStatsKey] = useState(0); // Key to force stats refresh

  const handleStatsUpdate = () => {
    setStatsKey(prev => prev + 1); // Increment key to trigger stats refresh
  };

  return (
    <div>
      {/* Stats Dashboard - Outside of List component */}
      <Box style={{ padding: '16px' }}>
        <FeatureStats key={statsKey} />
      </Box>
      
      {/* Features List */}
      <List 
        {...props} 
        title="Feature Management"
        filters={<FeatureFilters />}
        actions={<FeatureListActions />}
        perPage={25}
        resource="features"
      >
        <Datagrid optimized>
          <TextField source="name" label="Feature Name" />
          <TextField source="description" label="Description" />
          
          <FunctionField
            label="Status"
            render={record => (
              <Chip 
                label={record.status}
                color={getStatusColor(record.status)}
                size="small"
                variant="outlined"
              />
            )}
          />
          
          <FunctionField
            label="Global"
            render={record => (
              <Box display="flex" alignItems="center">
                {record.enabledGlobally ? (
                  <Chip 
                    icon={<Public />} 
                    label="All Users" 
                    color="success" 
                    size="small"
                  />
                ) : (
                  <Chip 
                    icon={<People />} 
                    label={`${record.userCount} users`} 
                    color="primary" 
                    size="small"
                    variant="outlined"
                  />
                )}
              </Box>
            )}
          />
          
          <FunctionField
            label="Total Users"
            render={record => (
              <Typography variant="body2">
                {record.totalUsers === 'all' ? 'All Users' : `${record.totalUsers} users`}
              </Typography>
            )}
          />
          
          <DateField source="lastUpdated" label="Last Updated" showTime />
          
          <FunctionField
            label="Actions"
            render={record => (
              <Box display="flex" style={{ gap: '8px' }}>
                <GlobalToggleButton record={record} onStatsUpdate={handleStatsUpdate} />
                <ManageUsersButton record={record} />
                <DisableAllButton record={record} onStatsUpdate={handleStatsUpdate} />
              </Box>
            )}
          />
        </Datagrid>
      </List>
    </div>
  );
};

// Main Features List Component (export for backward compatibility)
export const FeatureList = FeatureListWithStats;

// Legacy Create Component for backward compatibility (creates user-specific feature targets)
export const FeatureTargetCreate = (props) => (
  <Create {...props} title="Add User to Feature">
    <SimpleForm>
      <Typography variant="h6" gutterBottom>
        Add User-Specific Feature Access
      </Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        This creates user-specific feature access. For global features, use the main Features list instead.
      </Typography>
      
      <SelectInput 
        label="Feature Name" 
        source="feature" 
        choices={FEATURES} 
        validate={[required()]} 
      />
      <ReferenceInput source="userId" reference="users">
        <AutocompleteInput 
          optionText={choice => `${choice ? choice.email : ''} ${choice ? choice.social : ''}`}
          options={{ suggestionsContainerProps: { disablePortal: true } }} 
        />
      </ReferenceInput>
      <FileInput source="file" label="User segment (CSV)" accept="text/csv">
        <FileField source="file" title="CSV file" />
      </FileInput>
    </SimpleForm>
  </Create>
);

// Keep original exports for backward compatibility
export const FeatureTargetList = FeatureList;
